import type { LibraryBlockTypeForListPage } from '@ContractBuilder/types';
import type { DbLibraryBlock } from '@domain/schemas/block.schema';
import { getRequestAuthPayload } from '@helpers/getRequestAuth';
import type { GetBlockResponse, GetBlocksResponse } from '@responses/blocks';
import type { GetCsvUrlResponse } from '@responses/csv';
import { queryOptions, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import type { QueryFactory } from './factory.types';
import type { UseQueryRefetchFn, UseQueryStatus } from './types';

type Params = { [key: string]: string };
const getParams = (searchParams: URLSearchParams, params?: Params) => (paramKey: string) =>
  params?.[paramKey] ?? searchParams.get(paramKey);

export const fetchBlock = async (id: string): Promise<GetBlockResponse> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: `blocks/${id}`,
  });

  const response = await axios(networkPayload);
  return response.data;
};

export const fetchBlocks = async (_authToken: string, params?: Params): Promise<GetBlocksResponse> => {
  const { searchParams } = new URL(location.href);

  const getParamValue = getParams(searchParams, params);
  const page = getParamValue('page');
  const limit = getParamValue('limit');
  const sortBy = getParamValue('sortBy');
  const sortDirection = getParamValue('sortDirection');
  const filters = getParamValue('filters');
  const search = getParamValue('search') ?? '';
  const type = params?.type;

  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    params: { page, limit, sortBy, sortDirection, filters, search, type },
    url: '/blocks',
  });

  const response = await axios(networkPayload);
  return response.data;
};

export const blockQueries = {
  list: (params: Params = {}) =>
    queryOptions({
      queryKey: ['blocks', 'list', params] as const,
      queryFn: () => fetchBlocks('', params),
    }),
  detail: (id: string) =>
    queryOptions({
      queryKey: ['blocks', 'detail', id] as const,
      queryFn: () => fetchBlock(id),
    }),
} satisfies QueryFactory;

export const fetchBlocksCSVDownloadUrl = async (params?: Params): Promise<string> => {
  const type = params?.type;
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/blocks',
    params: { format: 'csv', type },
  });

  const response = await axios<GetCsvUrlResponse>(networkPayload);
  return response.data.downloadUrl;
};

interface BlockLibraryHook {
  data?: DbLibraryBlock;
  status: UseQueryStatus;
  isFetching: boolean;
  refetch: UseQueryRefetchFn<any, any>;
}

export const useBlockLibrary = (id: string, enabled = true): BlockLibraryHook => {
  const { data, status, isFetching, refetch } = useQuery({
    ...blockQueries.detail(id),
    enabled,
  });
  return { data: data?.data, status, isFetching, refetch };
};

interface BlocksLibraryHook {
  data?: LibraryBlockTypeForListPage[];
  names: string[]; // always empty; kept for API compatibility
  status: UseQueryStatus;
  isFetching: boolean;
  refetch: UseQueryRefetchFn<any, any>;
}

export const useBlocksLibrary = (params: Params = {}, enabled = true): BlocksLibraryHook => {
  const { data, status, isFetching, refetch } = useQuery({ ...blockQueries.list(params), enabled });
  return { data, names: [], status, isFetching, refetch };
};

export function useBlocks(params: Params = {}) {
  return useQuery(blockQueries.list(params));
}
